import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/authStore';
import Turnstile, { TURNSTILE_SITE_KEY, type TurnstileHandle } from '../../auth/Turnstile';
import { useTranslation } from '../../i18n/useTranslation';
import styles from './LandingPage.module.css';

// ─────────────────────────────────────────────────────────────────────────────

export default function LandingPage() {
  const navigate  = useNavigate();
  const { step, user, loading, error, requestCode, verifyCode, clearError, hydrate } = useAuthStore();
  const { t, language, setLanguage } = useTranslation();

  const [email, setEmail]   = useState('');
  const [code,  setCode]    = useState('');
  const codeRef             = useRef<HTMLInputElement>(null);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const captchaRef          = useRef<TurnstileHandle>(null);

  // ── Redirect if already authenticated ────────────────────────────────────
  useEffect(() => {
    // Check for expired session flag
    if (sessionStorage.getItem('session_expired') === 'true') {
      sessionStorage.removeItem('session_expired');
      useAuthStore.setState({ error: 'A sua sessão expirou por inatividade. Faça login novamente.' });
    }

    hydrate().then(() => {
      const u = useAuthStore.getState().user;
      if (u) navigate(u.role === 'admin' ? '/admin' : '/user', { replace: true });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step === 'authenticated' && user) {
      navigate(user.role === 'admin' ? '/admin' : '/user', { replace: true });
    }
  }, [step, user, navigate]);

  // Focus code input when transitioning to step 2
  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    // Token é de uso único: consome e já pede um novo desafio p/ a próxima tentativa
    requestCode(email.trim(), captcha).finally(() => captchaRef.current?.reset());
  };

  const handleCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    verifyCode(code.trim());
  };

  const handleCodeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits, max 6
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(val);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={styles.page}>
      {/* ── Background layers ── */}
      <div className={styles.bgGradient} />
      <div className={styles.bgGrid} />

      {/* ── Language toggle ── */}
      <div style={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 4, zIndex: 10 }}>
        {(['en', 'pt'] as const).map((lang) => (
          <button
            key={lang}
            onClick={() => setLanguage(lang)}
            style={{
              background: language === lang ? 'rgba(200,160,60,0.3)' : 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: language === lang ? '#fff' : '#ccc',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            {lang.toUpperCase()}
          </button>
        ))}
      </div>

      {/* ── Hero ── */}
      <header className={styles.hero}>
        <div className={styles.emblem}>
          <img src="/favicon.svg" alt="Dark Age Logo" width="80" height="80" />
        </div>
        <h1 className={styles.title}>Dark Age</h1>
        <p className={styles.subtitle}>
          {t('landing.subtitle')}
        </p>
      </header>

      {/* ── Feature highlights ── */}
      <ul className={styles.features}>
        {[
          { icon: '🗡', text: t('landing.feat1') },
          { icon: '🧙', text: t('landing.feat2') },
          { icon: '🏰', text: t('landing.feat3') },
          { icon: '⚡', text: t('landing.feat4') },
        ].map(({ icon, text }) => (
          <li key={text} className={styles.featureItem}>
            <span className={styles.featureIcon}>{icon}</span>
            <span>{text}</span>
          </li>
        ))}
      </ul>

      {/* ── Auth card ── */}
      <div className={styles.card}>
        {step !== 'code' ? (
          /* ── Step 1: e-mail ── */
          <>
            <h2 className={styles.cardTitle}>{t('landing.login')}</h2>
            <p className={styles.cardHint}>
              {t('landing.hintEmail')}
            </p>
            <form onSubmit={handleEmailSubmit} className={styles.form}>
              <label className={styles.label} htmlFor="email">{t('landing.email')}</label>
              <input
                id="email"
                type="email"
                className={styles.input}
                placeholder="seuemail@exemplo.com"
                value={email}
                onChange={e => { clearError(); setEmail(e.target.value); }}
                required
                autoFocus
                autoComplete="email"
              />
              <Turnstile ref={captchaRef} onToken={setCaptcha} language={language === 'pt' ? 'pt-br' : 'en'} />
              {error && <p className={styles.error}>{error}</p>}
              <button type="submit" className={styles.btn} disabled={loading || (!!TURNSTILE_SITE_KEY && !captcha)}>
                {loading ? t('landing.sending') : t('landing.access')}
              </button>
            </form>
          </>
        ) : (
          /* ── Step 2: magic code ── */
          <>
            <h2 className={styles.cardTitle}>{t('landing.verify')}</h2>
            <p className={styles.cardHint} dangerouslySetInnerHTML={{ __html: t('landing.hintCode').replace('{email}', useAuthStore.getState().email || '') }} />
            <form onSubmit={handleCodeSubmit} className={styles.form}>
              <label className={styles.label} htmlFor="code">{t('landing.accessCode')}</label>
              <input
                id="code"
                ref={codeRef}
                type="text"
                inputMode="numeric"
                className={`${styles.input} ${styles.inputCode}`}
                placeholder="000000"
                value={code}
                onChange={handleCodeInput}
                maxLength={6}
                autoComplete="one-time-code"
              />
              {error && <p className={styles.error}>{error}</p>}
              <button type="submit" className={styles.btn} disabled={loading || code.length !== 6}>
                {loading ? t('landing.verifying') : t('landing.confirm')}
              </button>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={() => { clearError(); setCode(''); useAuthStore.setState({ step: 'email', error: null }); }}
              >
                ← {t('landing.otherEmail')}
              </button>
            </form>
          </>
        )}
      </div>

      <footer className={styles.footer}>
        Dark Age © 2025 — RPG Tático Web
      </footer>
    </div>
  );
}
